import { useState } from "react";
import { createPortal } from "react-dom";
import {
  getClassicModels,
  getGenerationCreditCost,
  INTEGRATED_MODEL,
  INTEGRATED_MODEL_ID,
  AFFILIATE_DEFAULT_MODEL_ID,
  INTEGRATED_DURATION_MIN,
  INTEGRATED_DURATION_MAX,
  POWER_TIER_LABEL,
  CLASSIC_TIER_LABEL,
  isClassicSilentModel,
  type ModelId,
  type DurationOption,
} from "@/config/models";
import { useUserStore } from "@/stores/user-store";
import { cn } from "@/lib/utils";
import { Zap, Gem, Crown, Sparkles, Lock, AudioLines } from "lucide-react";
import { Link } from "react-router-dom";

interface ModelSelectorProps {
  value: ModelId;
  duration: DurationOption;
  onChange: (id: ModelId) => void;
  onDurationChange: (d: DurationOption) => void;
  integratedSeconds: number;
  onIntegratedSecondsChange: (seconds: number) => void;
  /** Affiliate free-credit path — only Classic Standard 5s is selectable. */
  lockToAffiliateDefault?: boolean;
  /**
   * When set (signed-out traffic), locked-model taps open Sign Up instead of
   * the purchase upsell — anonymous users can't buy a pack yet.
   */
  onRequireAuth?: () => void;
}

export function ModelSelector({
  value,
  duration,
  onChange,
  onDurationChange,
  integratedSeconds,
  onIntegratedSecondsChange,
  lockToAffiliateDefault = false,
  onRequireAuth,
}: ModelSelectorProps) {
  const { user } = useUserStore();
  const hasPurchased = user?.highest_package != null;
  const powerLocked = !hasPurchased;
  const isIntegrated = value === INTEGRATED_MODEL_ID;
  const isClassic = isClassicSilentModel(value);
  const [showUpgradeModal, setShowUpgradeModal] = useState<"power" | "affiliate" | "8s" | null>(null);

  const classicModels = getClassicModels(duration);
  const integratedCost = getGenerationCreditCost(INTEGRATED_MODEL, integratedSeconds);

  const gateOrUpsell = (kind: "power" | "affiliate" | "8s") => {
    if (onRequireAuth) {
      onRequireAuth();
      return;
    }
    setShowUpgradeModal(kind);
  };

  const selectModel = (id: ModelId) => {
    if (id === INTEGRATED_MODEL_ID && powerLocked) {
      gateOrUpsell("power");
      return;
    }
    if (lockToAffiliateDefault && id !== AFFILIATE_DEFAULT_MODEL_ID) {
      gateOrUpsell("affiliate");
      return;
    }
    onChange(id);
  };

  const switchDuration = (d: DurationOption) => {
    if (lockToAffiliateDefault && d !== "5s") {
      gateOrUpsell("affiliate");
      return;
    }
    if (d === duration) return;
    onDurationChange(d);
    if (!isClassicSilentModel(value)) return;
    const currentTier = getClassicModels(duration).find((m) => m.id === value)?.tier ?? "Standard";
    const match = getClassicModels(d).find((m) => m.tier === currentTier);
    if (match) onChange(match.id);
  };

  const handle8sClick = () => {
    if (lockToAffiliateDefault) gateOrUpsell("affiliate");
    else if (!hasPurchased) gateOrUpsell("8s");
    else switchDuration("8s");
  };

  const upgradeCopy =
    showUpgradeModal === "power"
      ? {
          title: `Unlock ${POWER_TIER_LABEL}`,
          body: (
            <>Purchase any gizmo pack to unlock <strong className="text-foreground">{POWER_TIER_LABEL}</strong> — 720p video with native audio, 5–15s.</>
          ),
        }
      : showUpgradeModal === "affiliate"
        ? {
            title: "Unlock More Models",
            body: (
              <>Your free gizmos cover <strong className="text-foreground">Classic Standard 5s</strong>. Purchase any pack to unlock {POWER_TIER_LABEL}, Premium, and 8s.</>
            ),
          }
        : {
            title: "Unlock 8-Second Videos",
            body: (
              <>Purchase any gizmo pack to unlock <strong className="text-foreground">8-second</strong> classic generations.</>
            ),
          };

  return (
    <div className="space-y-4">
      {lockToAffiliateDefault && (
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          {onRequireAuth ? (
            <>
              Free credits cover <span className="text-foreground font-semibold">Classic Standard 5s</span>
              . Sign up free to generate — unlock {POWER_TIER_LABEL} after any pack.
            </>
          ) : (
            <>
              Free credits cover <span className="text-foreground font-semibold">Classic Standard 5s</span>
              . Buy a pack to unlock other models.
            </>
          )}
        </p>
      )}

      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
          {POWER_TIER_LABEL}
        </p>
        <button
          type="button"
          onClick={() => selectModel(INTEGRATED_MODEL_ID)}
          className={cn(
            "group relative w-full overflow-hidden rounded-xl border-2 text-left transition-all",
            isIntegrated
              ? "border-[#d4a574]/55 shadow-[0_0_28px_rgba(212,165,116,0.22)]"
              : "border-[#d4a574]/20 hover:border-[#d4a574]/40",
            powerLocked && "opacity-90",
          )}
          style={{
            background: isIntegrated
              ? "linear-gradient(135deg, #3a2a1a 0%, #2a2035 45%, #1f1830 100%)"
              : "linear-gradient(135deg, rgba(212,165,116,0.12) 0%, rgba(42,32,53,0.85) 50%, rgba(26,21,32,0.9) 100%)",
          }}
        >
          <div
            className="pointer-events-none absolute inset-0 opacity-60"
            style={{
              background:
                "radial-gradient(ellipse at 15% 20%, rgba(212,165,116,0.28), transparent 45%), radial-gradient(ellipse at 90% 80%, rgba(180,120,255,0.12), transparent 40%)",
            }}
          />
          <div className="pointer-events-none absolute -right-6 -top-6 size-24 rounded-full bg-[#d4a574]/10 blur-2xl transition-opacity group-hover:opacity-100" />

          <div className="relative flex items-center gap-3 p-3.5">
            <div
              className={cn(
                "size-10 rounded-xl flex items-center justify-center shrink-0 border",
                isIntegrated
                  ? "bg-[#d4a574]/20 border-[#d4a574]/35 text-[#d4a574] shadow-[0_0_12px_rgba(212,165,116,0.25)]"
                  : "bg-[#d4a574]/10 border-[#d4a574]/20 text-[#d4a574]/80",
              )}
            >
              {powerLocked ? (
                <Lock className="size-4" strokeWidth={1.8} />
              ) : (
                <AudioLines className="size-4" strokeWidth={1.8} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className={cn("text-[13px] font-bold tracking-wide", isIntegrated ? "text-foreground" : "text-[#e8d5b8]")}>
                  {INTEGRATED_MODEL.label}
                </p>
                <span className="text-[9px] font-bold uppercase tracking-wider text-[#1a1520] bg-gradient-to-r from-[#d4a574] to-[#e8c090] px-1.5 py-0.5 rounded">
                  PRO
                </span>
              </div>
              <p className="text-[11px] text-[#d4a574]/70 mt-0.5 flex items-center gap-1.5">
                <Sparkles className="size-3 shrink-0" strokeWidth={2} />
                native audio · {INTEGRATED_DURATION_MIN}–{INTEGRATED_DURATION_MAX}s
              </p>
            </div>
            {isIntegrated && !powerLocked ? (
              <span className="text-[12px] font-semibold text-[#d4a574] tabular-nums shrink-0">
                {integratedCost} gizmos
              </span>
            ) : powerLocked ? (
              <span className="text-[10px] font-bold text-[#d4a574]/80 shrink-0">Unlock</span>
            ) : null}
          </div>
        </button>

        {isIntegrated && !powerLocked && (
          <div className="mt-2 rounded-xl border border-[#d4a574]/25 bg-gradient-to-b from-[#d4a574]/10 to-transparent p-3.5 space-y-2.5">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Clip length</p>
              <p className="text-[13px] font-bold text-foreground tabular-nums">
                {integratedSeconds}s
                <span className="text-muted-foreground font-semibold ml-2">· {integratedCost} gizmos</span>
              </p>
            </div>
            <input
              type="range"
              min={INTEGRATED_DURATION_MIN}
              max={INTEGRATED_DURATION_MAX}
              step={1}
              value={integratedSeconds}
              onChange={(e) => onIntegratedSecondsChange(Number(e.target.value))}
              className="w-full accent-[#d4a574] h-1.5 cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground/70 tabular-nums">
              <span>{INTEGRATED_DURATION_MIN}s</span>
              <span>{INTEGRATED_DURATION_MAX}s</span>
            </div>
          </div>
        )}
      </div>

      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
          {CLASSIC_TIER_LABEL}
        </p>

        <div className="space-y-2.5">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => switchDuration("5s")}
              className={cn(
                "flex-1 px-3 py-2 rounded-xl border-2 text-[12px] font-bold transition-all",
                duration === "5s" && isClassic
                  ? "bg-[#2a2035] border-[#d4a574]/40 text-foreground"
                  : "card-glass border-white/6 text-muted-foreground hover:border-white/12",
              )}
            >
              5s
            </button>
            <button
              type="button"
              onClick={handle8sClick}
              className={cn(
                "flex-1 px-3 py-2 rounded-xl border-2 text-[12px] font-bold transition-all flex items-center justify-center gap-1",
                duration === "8s" && isClassic
                  ? "bg-[#2a2035] border-[#d4a574]/40 text-foreground"
                  : "card-glass border-white/6 text-muted-foreground hover:border-white/12",
                (lockToAffiliateDefault || !hasPurchased) && "opacity-70",
              )}
            >
              <Crown className="size-3 text-[#d4a574]/70" strokeWidth={2} />
              8s
              <span className="text-[9px] font-bold text-[#d4a574]/70 bg-[#d4a574]/10 px-1 py-0.5 rounded">PRO</span>
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {classicModels.map((model) => {
              const Icon = model.tier === "Premium" ? Gem : Zap;
              const isSelected = value === model.id;
              const locked = lockToAffiliateDefault && model.id !== AFFILIATE_DEFAULT_MODEL_ID;
              return (
                <button
                  key={model.id}
                  type="button"
                  onClick={() => selectModel(model.id)}
                  className={cn(
                    "relative flex flex-col items-center p-3 rounded-xl border-2 transition-all text-center",
                    isSelected
                      ? "bg-[#2a2035] border-[#d4a574]/40"
                      : "card-glass border-white/6 hover:border-white/12",
                    locked && "opacity-60",
                  )}
                >
                  {locked && (
                    <Lock className="absolute top-2 right-2 size-3 text-muted-foreground" strokeWidth={2} />
                  )}
                  <Icon className={cn("size-3.5 mb-1", isSelected ? "text-[#d4a574]" : "text-muted-foreground")} strokeWidth={1.8} />
                  <span className={cn("text-[12px] font-bold", isSelected ? "text-foreground" : "text-muted-foreground")}>
                    {model.label}
                  </span>
                  <span className="text-[10px] text-muted-foreground tabular-nums">{model.creditCost} gizmos</span>
                </button>
              );
            })}
          </div>

          {hasPurchased && isClassic && (
            <button
              type="button"
              onClick={() => onChange(INTEGRATED_MODEL_ID)}
              className="mt-1 flex w-full items-start gap-2.5 rounded-xl border border-[#d4a574]/25 bg-[#d4a574]/[0.06] p-2.5 text-left transition-colors hover:bg-[#d4a574]/[0.1]"
            >
              <Sparkles className="size-3.5 shrink-0 text-[#d4a574] mt-0.5" strokeWidth={2} />
              <span className="min-w-0 flex-1 text-[11px] leading-relaxed text-[#e8d5b8]">
                Try <span className="font-bold text-foreground">{POWER_TIER_LABEL}</span> for native audio · 5–15s
              </span>
              <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-[#d4a574]">
                Switch
              </span>
            </button>
          )}
        </div>
      </div>

      {showUpgradeModal && createPortal(
        <div className="fixed inset-0 z-[9999]" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md" onClick={() => setShowUpgradeModal(null)} style={{ zIndex: 0 }} />
          <div
            className="relative rounded-3xl max-w-sm w-full mx-4 overflow-hidden shadow-2xl shadow-black/40 border border-[#d4a574]/15 animate-modal-in"
            style={{ zIndex: 1, background: "linear-gradient(170deg, #2a2035 0%, #1a1520 50%, #1a1520 100%)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative px-6 pt-7 pb-5 text-center">
              <div className="size-14 rounded-2xl bg-[#d4a574]/10 border border-[#d4a574]/20 flex items-center justify-center mx-auto mb-4">
                <Crown className="size-7 text-[#d4a574]" />
              </div>
              <h3 className="text-xl font-extrabold text-foreground mb-1">{upgradeCopy.title}</h3>
              <p className="text-[14px] text-muted-foreground leading-relaxed">{upgradeCopy.body}</p>
            </div>
            <div className="px-6 pb-6 flex flex-col gap-2">
              <Link to="/billing" className="btn-primary w-full py-3 rounded-xl text-[14px] text-center flex items-center justify-center gap-2">
                <Sparkles className="size-4" />
                Get Gizmos
              </Link>
              <button
                onClick={() => setShowUpgradeModal(null)}
                className="w-full py-2.5 rounded-xl text-[13px] font-semibold text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
